/** One wiki proposal: checks, line diff against the current page, Add or Discard (child route of the app shell) */

import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import type { DiffLine, WikiProposalChecks } from '@agentwiki/shared'
import { useDecideProposal, useWikiProposal } from '../hooks/use-project-content'
import { useAppStore } from '../stores/app-store'
import { projectPath, projectReviewPath } from '../components/shell/nav-config'
import { useT } from '../i18n'
import type { TranslationKey } from '../i18n'

function ChecksPanel({ checks }: { checks: WikiProposalChecks }) {
  const t = useT()
  const { citations, conflict, pinned, contradictions } = checks
  const issues: string[] = []
  if (conflict.stale) issues.push(t('review.check.stale', { current: conflict.currentVersion ?? '-', base: conflict.baseVersion ?? '-' }))
  if (pinned.changedAnchors.length) issues.push(t('review.check.pinned', { anchors: pinned.changedAnchors.join(', ') }))

  return (
    <section className="space-y-2 rounded-xl border border-border bg-surface-raised p-4 text-sm">
      <h2 className="font-semibold text-fg">{t('review.checks')}</h2>
      <ul className="space-y-1.5 text-fg-muted">
        <li>{t('review.check.citations', { cited: citations.cited, total: citations.total, percent: Math.round(citations.coverage * 100) })}</li>
        {citations.supersededSourceIds.length > 0 && (
          <li>{t('review.check.superseded', { ids: citations.supersededSourceIds.join(', ') })}</li>
        )}
        {issues.map((issue) => (
          <li key={issue} className="flex gap-2 text-danger"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{issue}</li>
        ))}
        {contradictions.map((c) => (
          <li key={`${c.removed}|${c.added}`} className="space-y-0.5">
            <span className="flex gap-2 text-fg">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden="true" />
              {t('review.check.contradiction', { reason: t(`review.reason.${c.reason}` as TranslationKey) })}
            </span>
            <span className="block pl-6 line-through">{c.removed}</span>
            <span className="block pl-6 text-fg">{c.added}</span>
          </li>
        ))}
        {checks.blocking.length === 0 && (
          <li className="flex gap-2 text-fg"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent-text" aria-hidden="true" />{t('review.check.ok')}</li>
        )}
      </ul>
    </section>
  )
}

const DIFF_STYLE: Record<DiffLine['op'], string> = {
  add: 'bg-accent-soft text-fg',
  del: 'bg-surface-muted text-fg-subtle line-through',
  same: 'text-fg-muted',
}

function DiffView({ diff }: { diff: DiffLine[] }) {
  const t = useT()
  if (!diff.some((d) => d.op !== 'same')) return <p className="text-sm text-fg-subtle">{t('review.noDiff')}</p>
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <pre className="min-w-full text-xs leading-relaxed">
        {diff.map((line, i) => (
          <div key={i} className={`whitespace-pre-wrap break-words px-3 ${DIFF_STYLE[line.op]}`}>
            <span aria-hidden="true" className="mr-2 select-none text-fg-subtle">{line.op === 'add' ? '+' : line.op === 'del' ? '-' : ' '}</span>
            {line.text || ' '}
          </div>
        ))}
      </pre>
    </div>
  )
}

export function ProjectReviewDetailPage() {
  const t = useT()
  const navigate = useNavigate()
  const locale = useAppStore((s) => s.locale)
  const { projectSlug = 'default', proposalId } = useParams<{ projectSlug: string; proposalId: string }>()
  const { data: proposal, isLoading, error } = useWikiProposal(projectSlug, proposalId)
  const decide = useDecideProposal(projectSlug)
  const [note, setNote] = useState('')

  const submit = (decision: 'apply' | 'discard') => {
    if (!proposal) return
    decide.mutate({ id: proposal.id, decision, note: note.trim() || undefined }, {
      onSuccess: () => {
        toast.success(t(decision === 'apply' ? 'review.added' : 'review.discarded'))
        navigate(decision === 'apply' ? projectPath(projectSlug, `wiki/${proposal.slug}`) : projectReviewPath(projectSlug))
      },
      onError: (err) => toast.error(t('review.failed', { message: err instanceof Error ? err.message : String(err) })),
    })
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 px-4 py-6 md:px-8 md:py-10">
      <Link to={projectReviewPath(projectSlug)} className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('sources.tab.review')}
      </Link>
      {isLoading ? (
        <p className="text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : error || !proposal ? (
        <p role="alert" className="text-sm text-danger">{t('review.notFound')}</p>
      ) : (
        <>
          <header className="space-y-1">
            <h1 className="break-words font-display text-2xl font-semibold tracking-tight text-fg">{proposal.title}</h1>
            <p className="text-sm text-fg-subtle">
              {proposal.slug} · {proposal.baseVersion ? t('review.basedOn', { version: proposal.baseVersion }) : t('review.newPage')}
              {' · '}{t(proposal.actorType === 'api_key' ? 'review.by.agent' : 'review.by.user')}
              {' · '}{new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(proposal.createdAt))}
            </p>
            {proposal.changeSummary && <p className="text-sm text-fg-muted">{proposal.changeSummary}</p>}
            {proposal.status !== 'pending' && (
              <p className="text-sm text-fg-muted">
                {t('review.decided', { status: t(`review.status.${proposal.status}` as TranslationKey), by: proposal.decidedBy ?? '-' })}
                {proposal.decisionNote ? ` · ${proposal.decisionNote}` : ''}
              </p>
            )}
          </header>

          <ChecksPanel checks={proposal.checks} />

          <section className="space-y-2">
            <h2 className="font-semibold text-fg">{t('review.diff')}</h2>
            {proposal.status === 'pending'
              ? <DiffView diff={proposal.diff} />
              : <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-xl border border-border bg-surface-muted p-4 text-xs text-fg">{proposal.content}</pre>}
          </section>

          {proposal.status === 'pending' && (
            <section className="flex flex-col gap-3 rounded-xl border border-border p-4 md:flex-row md:items-end">
              <label className="min-w-0 flex-1 space-y-1 text-sm text-fg-muted">
                <span>{t('review.note')}</span>
                <input
                  value={note}
                  maxLength={500}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-fg focus:border-accent"
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={decide.isPending}
                  onClick={() => submit('discard')}
                  className="cursor-pointer rounded-lg border border-border px-3.5 py-2 text-sm font-medium text-fg hover:bg-surface-hover disabled:opacity-60"
                >
                  {t('review.discard')}
                </button>
                <button
                  type="button"
                  disabled={decide.isPending || proposal.checks.blocking.length > 0}
                  onClick={() => submit('apply')}
                  className="cursor-pointer rounded-lg bg-accent px-3.5 py-2 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {decide.isPending ? t('review.working') : t('review.add')}
                </button>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}
